<template>
	<modal-side-generic
	:headerColor="headerColor"
	:title="selectedProcess.externalId"
	:subTitle="amountFormat(Math.abs(selectedProcess.amount), selectedProcess.currency.externalId)"
	:bigIcon="headerBigIcon"
	@hideMe="emit('hideMe')">
		<div class="details-helper-wrapper" v-if="!showLoader && !applyErrorState">
			<div
			v-for="(lineGroup, index) in processDetail"
			class="group-wrapper"
			:key="`lineGroup${lineGroup.index}`"
			:class="{ 'with-table': lineGroup.hasTable }"
			:style="{ 'padding': lineGroup.hasTable ? '0px': null, 'margin-bottom': lineGroup.hasTable ? '0px': null }">
				<!-- Divider -->
				<divider-generic
				:icon="lineGroup.icon"
				:title="lineGroup.label"
				:applyBorder="index !== 0"/>
				<!-- Blocks -->
				<div
				v-for="(line, index) in lineGroup.lines"
				:key="`lineGroupLine${line, index}`"
				class="line-content">
					<!-- Show card with Info -->
					<infobox-generic
					v-if="line.card"
					:label="line.card"/>
					<!-- Text Block for each line -->
					<text-block-generic
					v-if="!line.card"
					applyEllipsis
					:params="line"
					:icon="line.icon"
					:colorIconPreset="line.colorIconPreset"
					:mask="line.mask"/>
				</div>
			</div>
        </div>
        <!-- Loader Generic -->
        <loaders-generic v-if="showLoader" fullSize/>
        <!-- Detail Error State -->
        <empty-state-error v-if="applyErrorState && !showLoader" :message="t('transfers.requests.internationalCorporate.detailModal.movementDetailError')"/>
	</modal-side-generic>

	<!-- FeedbackToast -->
	<toast-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
</template>

<script setup>
/* Generic Components */
import ModalSideGeneric from '@/components/generic/modals/side/Generic'
import DividerGeneric from '@/components/generic/dividers/Generic'
import TextBlockGeneric from '@/components/generic/textBlocks/Generic'
import InfoboxGeneric from '@/components/generic/infobox/Generic'
import LoadersGeneric from '@/components/generic/loaders/Generic'
import EmptyStateError from '@/components/generic/emptyStates/Error'
import ToastGeneric from '@/components/generic/toasts/Generic'

/* Vue & Helpers */
import { ref, computed } from 'vue'
import { amountFormat } from '@/assets/scripts/amounts'
import { getDateWithTime } from '@/assets/scripts/dateBuilders'
import { ACCOUNT_NUMBER, TMX_IBAN, PHONE_NUMBER_WITH_3_INDICATIVE } from '@/assets/constants/masks'
import { flagCurrenciesIconMapper } from '@/assets/scripts/mappers/flags'
import { ACCOUNT_MAIN_CURRENCY } from '@/assets/constants/accounts'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'
import { processIconHandler, processClassesHandler, iconModalMapper, colorStatusMapperModal } from '@/assets/scripts/mappers/internationalCorporate'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['hideMe'])

/** Props
	@selectedProcess { Object }
*/
const props = defineProps({
	selectedProcess: Object
})

/* mapActions & mapGetters */
const { requestTmxEstimatedAmount } = mapActions(moduleMapper.TRANSFERS_REQUESTS_INTERNATIONAL_CORPORATE)
const { getTmxEstimatedAmount } = mapGetters(moduleMapper.TRANSFERS_REQUESTS_INTERNATIONAL_CORPORATE)

/* Data */
const showLoader = ref(false)
const applyErrorState = ref(false)

/*************************************************
*                                                *
*                Side Modal Info                 *
*                                                *
*************************************************/
const headerColor = computed(() => {
	return colorStatusMapperModal(props.selectedProcess.statusCode)
})

const headerBigIcon = computed(() => {
	return iconModalMapper(props.selectedProcess.statusCode)
})

/*************************************************
*                                                *
*               Process Detail Data              *
*                                                *
*************************************************/
const processDetail = computed(() => {
	return [{
		label: t('transfers.requests.internationalCorporate.detailModal.info'),
		icon: 'info',
		lines: [{
			label: t('transfers.requests.internationalCorporate.detailModal.ref'),
			content: props.selectedProcess.externalId ? props.selectedProcess.externalId : '-'
		}, {
			label: t('transfers.requests.internationalCorporate.detailModal.state'),
			content: props.selectedProcess.statusDescription,
			icon: processIconHandler(props.selectedProcess.statusCode),
			colorIconPreset: processClassesHandler(props.selectedProcess.statusCode)
		}, {
			label: t('transfers.requests.internationalCorporate.detailModal.startDate'),
			content: getDateWithTime(props.selectedProcess.creationDate),
			classes: 'capitalize'
		}, {
			label: t('transfers.requests.internationalCorporate.detailModal.channel'),
			content: props.selectedProcess.customerOperation.channel.description
		}]
	}, {
		label: t('transfers.requests.internationalCorporate.detailModal.originAccount'),
		icon: 'account-balance',
		lines: [{
			label: t('transfers.requests.internationalCorporate.detailModal.account'),
			content: props.selectedProcess.originAccount.accountNumber ? props.selectedProcess.originAccount.accountNumber : '-',
			mask: props.selectedProcess.originAccount.accountNumber ? ACCOUNT_NUMBER : null
		}]
	}, {
		label: t('transfers.requests.internationalCorporate.detailModal.beneficiary.beneficiary'),
		icon: 'person',
		lines: [{
			label: t('transfers.requests.internationalCorporate.detailModal.beneficiary.iban'),
			content: props.selectedProcess.beneficiary.accountInternationalNumber ? props.selectedProcess.beneficiary.accountInternationalNumber : props.selectedProcess.beneficiary.accountNumber,
			mask: props.selectedProcess.beneficiary.accountInternationalNumber ? TMX_IBAN : ACCOUNT_NUMBER
		}, {
			label: t('transfers.requests.internationalCorporate.detailModal.beneficiary.swift'),
			content: props.selectedProcess.beneficiary.swiftCode ? props.selectedProcess.beneficiary.swiftCode : '-'
		}, {
			label: t('transfers.requests.internationalCorporate.detailModal.beneficiary.name'),
			content: props.selectedProcess.beneficiary.name ? props.selectedProcess.beneficiary.name : '-',
			classes: 'capitalize'
		}, {
			label: t('transfers.requests.internationalCorporate.detailModal.beneficiary.fiscalDocumentNumber'),
			content: props.selectedProcess.beneficiary.fiscalDocumentNumber ? props.selectedProcess.beneficiary.fiscalDocumentNumber : '-'
		}, {
			label: t('transfers.requests.internationalCorporate.detailModal.beneficiary.phoneNumber'),
			content: props.selectedProcess.beneficiary.phoneNumber ? props.selectedProcess.beneficiary.phoneNumber : '-',
			mask: props.selectedProcess.beneficiary.phoneNumber ? PHONE_NUMBER_WITH_3_INDICATIVE : null
		}, {
			label: t('transfers.requests.internationalCorporate.detailModal.beneficiary.email'),
			content: props.selectedProcess.beneficiary.email ? props.selectedProcess.beneficiary.email : '-'
		}, {
			label: t('transfers.requests.internationalCorporate.detailModal.beneficiary.country'),
			content: props.selectedProcess.beneficiary.country.description ? props.selectedProcess.beneficiary.country.description : '-',
			classes: 'capitalize'
		}, {
			label: t('transfers.requests.internationalCorporate.detailModal.beneficiary.zipCode'),
			content: props.selectedProcess.beneficiary.zipCode ? props.selectedProcess.beneficiary.zipCode : '-'
		}, {
			label: t('transfers.requests.internationalCorporate.detailModal.beneficiary.city'),
			content: props.selectedProcess.beneficiary.city ? props.selectedProcess.beneficiary.city : '-',
			classes: 'capitalize'
		}, {
			label: t('transfers.requests.internationalCorporate.detailModal.beneficiary.address'),
			content: props.selectedProcess.beneficiary.address ? props.selectedProcess.beneficiary.address : '-',
			classes: 'capitalize'
		}, {
			label: t('transfers.requests.internationalCorporate.detailModal.beneficiary.door'),
			content: props.selectedProcess.beneficiary.door ? props.selectedProcess.beneficiary.door : '-'
		}, {
			label: t('transfers.requests.internationalCorporate.detailModal.beneficiary.floor'),
			content: props.selectedProcess.beneficiary.floor ? props.selectedProcess.beneficiary.floor : '-'
		}]
	}, {
		label: t('transfers.requests.internationalCorporate.detailModal.amount'),
		icon: 'money',
		lines: [{
			label: t('transfers.requests.internationalCorporate.detailModal.amountToTransfer'),
			content: props.selectedProcess.amount ? amountFormat(Math.abs(props.selectedProcess.amount)) : '-'
		}, {
			label: t('transfers.requests.internationalCorporate.detailModal.currency'),
			icon: 'flags/' + flagCurrenciesIconMapper(props.selectedProcess.currency.externalId),
			content: props.selectedProcess.currency.externalId ? props.selectedProcess.currency.externalId : '-'
		}, {
			label: t('transfers.requests.internationalCorporate.detailModal.estimatedAmount'),
			content: getTmxEstimatedAmount.value.totalOperationValue ? amountFormat(getTmxEstimatedAmount.value.totalOperationValue, ACCOUNT_MAIN_CURRENCY) : '-'
		}, {
			card: t('transfers.requests.internationalCorporate.detailModal.estimatedAmountInfo')
		}]
	}, {
		label: t('transfers.requests.internationalCorporate.detailModal.operation'),
		icon: 'tag',
		lines: [{
			label: t('transfers.requests.internationalCorporate.detailModal.operationPurpose'),
			content: props.selectedProcess.operationPurpose.description || '-'
		}, {
			label: t('transfers.requests.internationalCorporate.detailModal.operationType'),
			content: props.selectedProcess.operationType.description || '-'
		}, {
			label: t('transfers.requests.internationalCorporate.detailModal.operationCategory'),
			content: props.selectedProcess.operationCategory.description || '-'
		}, {
			label: t('transfers.requests.internationalCorporate.detailModal.expensesType'),
			content: props.selectedProcess.expensesType.description || '-'
		}]
	}]
})

/************************************************
*												*
*                 Requests Logic                *
*												*
************************************************/
const requestEstimatedAmount = async () => {
	showLoader.value = true
	applyErrorState.value = false
	showFeedbackToast.value = false

	try {
		await requestTmxEstimatedAmount({
			value: props.selectedProcess.amount,
			currencyOrigin: props.selectedProcess.currency.externalId,
			currencyDestination: ACCOUNT_MAIN_CURRENCY
		})
		showLoader.value = false
	} catch (error) {
		if (error.toString() === 'Cancel') return
		applyErrorState.value = true
		errorHandler(error.response.data, requestEstimatedAmount)
	}
}

/************************************************
*												*
*                 Error Feedback                *
*												*
************************************************/
const showFeedbackToast = ref(false)

const feedbackToastData = ref(emptyObjectConstructor())

/************************************************
*												*
*             Success/Error Handlers            *
*												*
************************************************/
const errorHandler = (response, retryFn) => {
	showLoader.value = false

	feedbackToastData.value = errorObjectConstructor(response, retryFn)
	showFeedbackToast.value = true
}

requestEstimatedAmount()
</script>

<style lang="scss" scoped>
.details-helper-wrapper {
    display: flex;
    flex-direction: column;
}
</style>
